import React, { useEffect, useState } from "react";
import general from "../styles/General.module.scss";
import { useGlobalContext } from "../contextProvider/ContextProvider";

const Wunschtermin = ({ handleInputChange, inputSummary }) => {
  const {
    minCalendar,
    maxCalendar,
    workingdays,
    vacation,
    exclusionDates,
    setIsDateSelected,
  } = useGlobalContext();

  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const storedDate = inputSummary["wunschtermin"];
    if (storedDate) {
      const [day, month, year] = storedDate.split(".").map(Number);
      if (
        !isNaN(day) &&
        !isNaN(month) &&
        !isNaN(year) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= daysInMonth(month, year)
      ) {
        setSelectedDate(day);
        setIsDateSelected(true);
      }
    }
  }, [inputSummary, setIsDateSelected]);

  const daysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const getMonthName = (month) => {
    const monthNames = [
      "Januar",
      "Februar",
      "März",
      "April",
      "Mai",
      "Juni",
      "Juli",
      "August",
      "September",
      "Oktober",
      "November",
      "Dezember",
    ];
    return monthNames[month];
  };

  const getAbbreviatedWeekdays = () => {
    const weekdays = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
    return weekdays.map((day) => (
      <div key={day} className="weekday">
        {day}
      </div>
    ));
  };

  const parseExclusionDates = (exclusionDatesString) => {
    if (!exclusionDatesString) {
      return [];
    }
    try {
      const parsedDates = JSON.parse(exclusionDatesString);
      return parsedDates.map((dateString) => {
        const [day, month, year] = dateString.split(".").map(Number);
        return new Date(year, month - 1, day);
      });
    } catch (error) {
      console.error("Error parsing exclusionDates:", error);
      return [];
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + minCalendar);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + maxCalendar);

    const isInDateRange = date >= minDate && date <= maxDate;

    const weekdayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
    const dateWeekday = weekdayNames[date.getDay()];
    const isWorkingDay =
      workingdays.includes(dateWeekday) ||
      (workingdays.includes("Wochenende") &&
        (dateWeekday === "So" || dateWeekday === "Sa"));

    const vacationDates = [
      { day: 1, month: 0 }, // 1st January
      { day: 1, month: 4 }, // 1st May
      { day: 3, month: 9 }, // 3rd October
      { day: 24, month: 11 }, // 24th December
      { day: 25, month: 11 }, // 25th December
      { day: 26, month: 11 }, // 26th December
    ];
    const isVacation =
      vacation &&
      vacationDates.some(
        (vacationDate) =>
          vacationDate.day === date.getDate() &&
          vacationDate.month === date.getMonth()
      );

    const parsedExclusionDates = parseExclusionDates(exclusionDates);
    const isExcluded = parsedExclusionDates.some(
      (excludedDate) =>
        excludedDate.getDate() === date.getDate() &&
        excludedDate.getMonth() === date.getMonth() &&
        excludedDate.getFullYear() === date.getFullYear()
    );

    const isDisabled = !(
      isInDateRange &&
      isWorkingDay &&
      !isVacation &&
      !isExcluded
    );

    return isDisabled;
  };

  const handleClickWunschtermin = (day) => {
    const selectedDate = new Date(currentYear, currentMonth, day);
    if (!isDateDisabled(selectedDate)) {
      setSelectedDate(day);
      handleInputChange(
        "wunschtermin",
        `${day}.${currentMonth + 1}.${currentYear}`
      );
    }
  };

  const changeMonth = (value) => {
    setCurrentMonth(currentMonth + value);
    setSelectedDate(null); // Reset selected date when changing month
  };

  const changeYear = (value) => {
    setCurrentYear(currentYear + value);
    setSelectedDate(null); // Reset selected date when changing year
  };

  const renderCalendar = () => {
    const totalDays = daysInMonth(currentMonth + 1, currentYear);
    const monthName = getMonthName(currentMonth);
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    const days = [];
    // Push empty placeholders for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="empty-day"></div>);
    }

    for (let day = 1; day <= totalDays; day++) {
      const currentDate = new Date(currentYear, currentMonth, day);
      const isSelected = selectedDate === day;
      const storedDate = inputSummary["wunschtermin"];
      const storedDateObj = storedDate ? new Date(storedDate) : null;
      const isSelectedFromStorage =
        storedDateObj &&
        currentDate.toDateString() === storedDateObj.toDateString();
      const isDisabled = isDateDisabled(currentDate);

      days.push(
        <div
          key={day}
          className={`day ${isSelected ? "selected" : ""} ${
            isSelectedFromStorage ? "stored" : ""
          } ${isDisabled ? "disabled" : ""}`}
          onClick={() => handleClickWunschtermin(day)}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="calendarWrapper">
        <div className="calendar">
          <div className="header">
            <button
              onClick={() => changeMonth(-1)}
              className="calendarPrev"
            ></button>
            <div style={{ display: "flex", flexWrap: "no-wrap", gap: "5px" }}>
              <p
                className={`${general.bold} ${general.noMargin} ${general.fs18}`}
              >
                {monthName} {currentYear}
              </p>
              <div className={general.monthNavigation}>
                <button
                  onClick={() => changeYear(-1)}
                  className="calendarPrevMonth"
                ></button>
                <button
                  onClick={() => changeYear(1)}
                  className="calendarNextMonth"
                ></button>
              </div>
            </div>
            <button
              onClick={() => changeMonth(1)}
              className="calendarNext"
            ></button>
          </div>
          <div className="weekdays">{getAbbreviatedWeekdays()}</div>
          <div className="days">{days}</div>
        </div>
      </div>
    );
  };
  return renderCalendar();
};

export default Wunschtermin;
